import { useEnhancedImageSource, useRefineHistoryContext } from "@design-lab/entities/artwork";
import { useCompleteRefineSession } from "@design-lab/features/design-artwork/api/useManualBackgroundRemoval";
import {
  trackImageEditToolCancelled,
  trackImageEditToolConfirm,
  trackImageEditToolError
} from "@design-lab/shared/lib/analytics";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { Button } from "@design-lab/shared/ui/design-system/Button";

type RefineActionBarProps = {
  onCancel: () => void;
  onConfirm: () => void;
  placement: PlacementFormPosition;
};

export function RefineActionBar({ onCancel, onConfirm, placement }: RefineActionBarProps) {
  const { hasHistory, currentOperation, currentImageData } = useRefineHistoryContext();

  const side = placement === "back" ? "back" : "front";
  const { source, artworkId } = useEnhancedImageSource({ side });
  const completeRefineSession = useCompleteRefineSession(side);

  const isConfirmEnabled =
    hasHistory && currentOperation?.type !== "baseline" && !completeRefineSession.isPending;

  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;

    // Get artwork ID from source or artworkId
    const artwork = source.enhancementTier === "full_enhancements" ? source.enhancedImage : null;
    const dtfArtworkId = artwork ? Number(artworkId) : undefined;
    if (!dtfArtworkId) return;

    try {
      let mutateParams;

      if (hasHistory && currentImageData) {
        // Convert base64 data URL to blob
        const response = await fetch(currentImageData);
        const blob = await response.blob();

        mutateParams = {
          dtf_artwork_id: dtfArtworkId,
          image: blob,
          manual_edit: true,
          remove_background: false,
          start_new_edit: false
        };
      } else {
        // If no changes, complete session without image
        mutateParams = {
          dtf_artwork_id: dtfArtworkId,
          manual_edit: false,
          remove_background: false,
          start_new_edit: false
        };
      }

      await completeRefineSession.mutateAsync(mutateParams);

      trackImageEditToolConfirm(dtfArtworkId);

      onConfirm();
    } catch (error: any) {
      console.error("Failed to save refine changes:", error);

      const errorMessage =
        error.response?.data?.error ?? (error instanceof Error ? error.message : String(error));
      trackImageEditToolConfirm(dtfArtworkId, errorMessage);
      trackImageEditToolError("refine_action_bar", errorMessage, {
        error,
        artworkId: dtfArtworkId,
        component: "RefineActionBar"
      });
    }
  };

  const handleCancel = () => {
    if (artworkId) {
      trackImageEditToolCancelled(artworkId);
    }
    onCancel();
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-end bg-white p-2">
      <Button
        variant="link"
        className="text-blue-700 no-underline hover:text-blue-800 hover:no-underline"
        onPress={handleCancel}
        isDisabled={completeRefineSession.isPending}
      >
        Cancel
      </Button>
      <Button
        className="bg-blue-700 text-white hover:bg-blue-800 disabled:bg-blue-700/50"
        onPress={handleConfirm}
        isDisabled={!isConfirmEnabled}
      >
        {completeRefineSession.isPending ? "Saving..." : "Confirm Changes"}
      </Button>
    </div>
  );
}
